import { useState } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import { useNotify } from "react-admin";
import { callApi } from "@/dataprovider/misc-apis";
import { apiErrorMessage } from "../shared/api-error";

type YearOption = { id: string; name: string };

/** Copies another year's fees into the current one without overwriting anything already set. */
export function CopyFeesDialog({
  open,
  onClose,
  onCopied,
  toYear,
  years,
}: {
  open: boolean;
  onClose: () => void;
  onCopied: () => void;
  toYear: YearOption | undefined;
  years: YearOption[];
}) {
  const notify = useNotify();
  const [fromId, setFromId] = useState("");
  const [busy, setBusy] = useState(false);
  const sources = years.filter((y) => y.id !== toYear?.id);

  const submit = async () => {
    if (!toYear || !fromId) return;
    setBusy(true);
    try {
      const response = await callApi({
        location: "feeStructures/copy-from-year",
        method: "POST",
        data: { fromAcademicYearId: fromId, toAcademicYearId: toYear.id },
      });
      const r = response.data as {
        copied: number;
        skippedExisting: number;
        skippedInactive: number;
      };
      const skipped = r.skippedExisting + r.skippedInactive;
      notify(
        `Copied ${r.copied} ${r.copied === 1 ? "fee" : "fees"}${skipped ? `; ${skipped} skipped (already set, or inactive class/fee)` : ""}.`,
        { type: r.copied ? "success" : "info" }
      );
      onCopied();
      onClose();
    } catch (error: unknown) {
      notify(apiErrorMessage(error, "Could not copy the fees"), { type: "error" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle>Copy fees into {toYear?.name}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Fees already set for {toYear?.name} are kept as they are; only missing ones are added. The
          dues already generated for students do not change until you sync them.
        </DialogContentText>
        <TextField
          select
          label="Copy from"
          value={fromId}
          onChange={(e) => setFromId(e.target.value)}
          fullWidth
        >
          {sources.map((y) => (
            <MenuItem key={y.id} value={y.id}>
              {y.name}
            </MenuItem>
          ))}
        </TextField>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button onClick={submit} variant="contained" disabled={busy || !fromId}>
          {busy ? "Copying…" : "Copy fees"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
